import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IconArrowLeft, IconBolt, IconMail, IconPlus, IconTrash } from '../components/icons';
import { CodeTabs, Empty, Field, Skeleton, useAsync, useToast } from '../components/ui';
import { api, ApiError, Template, TemplatePreview, TemplateVersion } from '../lib/api';
import { ago } from '../lib/format';
import { canWrite, useSession } from '../lib/session';
import { t } from '../lib/i18n';

export function TemplatesPage() {
  const { me } = useSession();
  const nav = useNavigate();
  const list = useAsync(() => api.get<Template[]>('/templates'));
  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('Шаблоны')}</h1>
          <p>{t('Письма, которые код отправляет по алиасу с переменными. Каждое сохранение — новая версия; отправленное письмо помнит свою.')}</p>
        </div>
        <Link to="/templates/generate" className="btn"><IconBolt size={16} /> Code2Email</Link>
        {canWrite(me) && <Link to="/templates/new" className="btn btn-primary"><IconPlus size={16} /> {t('Новый шаблон')}</Link>}
      </div>
      <div className="card">
        {list.loading ? (
          <div className="card-pad stack"><Skeleton /><Skeleton /></div>
        ) : !list.data?.length ? (
          <Empty icon={<IconMail size={36} />} title={t('Шаблонов пока нет')} action={<Link to="/templates/new" className="btn btn-primary">{t('Создать шаблон')}</Link>}>
            {t('Вынесите вёрстку чеков и кодов из кода: разработчик передаёт только алиас и переменные.')}
          </Empty>
        ) : (
          <table className="table">
            <thead><tr><th>{t('Название')}</th><th>{t('Алиас')}</th><th>{t('Тема')}</th><th>{t('Переменные')}</th><th>{t('Версия')}</th><th>{t('Изменён')}</th></tr></thead>
            <tbody>
              {list.data.map((t) => (
                <tr key={t.id} className="click" onClick={() => nav(`/templates/${t.id}`)}>
                  <td><strong>{t.name}</strong></td>
                  <td><code>{t.alias}</code></td>
                  <td className="truncate muted">{t.subject}</td>
                  <td className="small">{t.variables.map((v) => <code key={v} style={{ marginRight: 6 }}>{v}</code>)}</td>
                  <td><span className="pill sent">v{t.version}</span></td>
                  <td className="small muted">{ago(t.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

const STARTER = {
  name: 'Заказ оплачен',
  alias: 'order-paid',
  subject: 'Заказ №{{order.id}} оплачен',
  html: `<div style="font-family:Segoe UI,Arial,sans-serif;max-width:520px;margin:auto;padding:32px;background:#ffffff;border-radius:16px">
  <h1 style="color:#04162f;font-size:24px;margin:0 0 12px">Спасибо, {{name}}! 🌊</h1>
  <p style="color:#33506e;font-size:16px;line-height:1.6">Заказ №{{order.id}} на сумму {{order.total}} оплачен. Мы уже собираем его.</p>
  <a href="{{order.url}}" style="display:inline-block;margin-top:16px;background:#0b5cad;color:#fff;padding:12px 20px;border-radius:10px;text-decoration:none;font-weight:700">Открыть заказ</a>
</div>`,
  text: 'Спасибо, {{name}}! Заказ №{{order.id}} на сумму {{order.total}} оплачен: {{order.url}}',
};

/** Builds a sample-variables object from dotted names: ["order.id"] → { order: { id: "order.id" } } */
function sampleFor(names: string[], prev: Record<string, unknown>) {
  const out: Record<string, unknown> = JSON.parse(JSON.stringify(prev));
  for (const n of names) {
    const parts = n.split('.');
    let cur = out;
    parts.forEach((p, i) => {
      if (i === parts.length - 1) {
        if (cur[p] === undefined || typeof cur[p] === 'object') cur[p] = cur[p] ?? `‹${n}›`;
      } else {
        if (typeof cur[p] !== 'object' || cur[p] === null) cur[p] = {};
        cur = cur[p] as Record<string, unknown>;
      }
    });
  }
  return out;
}

export function TemplateEditorPage() {
  const { id = 'new' } = useParams();
  const isNew = id === 'new';
  const { me } = useSession();
  const toast = useToast();
  const nav = useNavigate();
  const writable = canWrite(me);

  const [form, setForm] = useState(isNew ? STARTER : null as null | typeof STARTER);
  const [version, setVersion] = useState<number | null>(null);
  const [vars, setVars] = useState('{\n  "name": "Анна",\n  "order": { "id": 1042, "total": "1 990 ₽", "url": "https://coffee.example/orders/1042" }\n}');
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [tab, setTab] = useState<'html' | 'text'>('html');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const versions = useAsync(() => (isNew ? Promise.resolve([]) : api.get<TemplateVersion[]>(`/templates/${id}/versions`)), [id, version]);

  useEffect(() => {
    if (isNew) return;
    api.get<Template>(`/templates/${id}`).then((t) => {
      setForm({ name: t.name, alias: t.alias, subject: t.subject, html: t.html ?? '', text: t.text ?? '' });
      setVersion(t.version);
    });
  }, [id, isNew]);

  const parsedVars = useMemo(() => {
    try {
      return { ok: true as const, value: JSON.parse(vars || '{}') as Record<string, unknown> };
    } catch {
      return { ok: false as const, value: {} };
    }
  }, [vars]);

  // Live preview, debounced; rendering happens on the server with the exact production renderer.
  useEffect(() => {
    if (!form || !parsedVars.ok) return;
    const t = setTimeout(() => {
      api
        .post<TemplatePreview>('/templates/preview', { subject: form.subject, html: form.html || null, text: form.text || null, variables: parsedVars.value })
        .then(setPreview)
        .catch(() => undefined);
    }, 300);
    return () => clearTimeout(t);
  }, [form, parsedVars]);

  if (!form) return <div className="stack"><Skeleton h={32} w={300} /><Skeleton h={300} /></div>;
  const set = (k: keyof typeof STARTER) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  async function save() {
    if (!form) return;
    setSaving(true);
    setErrors({});
    try {
      const body = { name: form.name, alias: form.alias, subject: form.subject, html: form.html || null, text: form.text || null };
      const tpl = isNew ? await api.post<Template>('/templates', body) : await api.patch<Template>(`/templates/${id}`, body);
      toast(isNew ? t('Шаблон создан') : t('Сохранено как версия {v}', { v: tpl.version }));
      setVersion(tpl.version);
      if (isNew) nav(`/templates/${tpl.id}`, { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.details) setErrors(Object.fromEntries(e.details.map((d) => [d.path, d.message])));
      toast(e instanceof ApiError && e.code === 'TEMPLATE_ALIAS_TAKEN' ? t('Такой алиас уже занят') : (e as Error).message, 'bad');
    } finally {
      setSaving(false);
    }
  }
  async function remove() {
    if (!confirm(t('Удалить шаблон? Отправка по этому алиасу начнёт возвращать 404.'))) return;
    await api.del(`/templates/${id}`);
    nav('/templates');
  }

  const sendBody = JSON.stringify({ from: 'orders@ваш-домен.com', to: 'anna@example.com', template: { alias: form.alias, variables: parsedVars.value } }, null, 2);

  return (
    <>
      <Link to="/templates" className="small row" style={{ marginBottom: 12 }}><IconArrowLeft size={14} /> {t('Все шаблоны')}</Link>
      <div className="page-head">
        <div className="grow">
          <div className="row">
            <h1>{isNew ? t('Новый шаблон') : form.name}</h1>
            {version && <span className="pill sent">v{version}</span>}
          </div>
          <p>{t('Переменные:')} <code>{'{{name}}'}</code> {t('экранируются,')} <code>{'{{{html}}}'}</code> {t('вставляются как есть,')} <code>{'{{order.id}}'}</code> — {t('вложенные поля.')}</p>
        </div>
        {!isNew && writable && <button className="btn btn-danger" onClick={remove}><IconTrash size={16} /></button>}
        {writable && <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? t('Сохраняем…') : isNew ? t('Создать') : t('Сохранить версию')}</button>}
      </div>

      <div className="editor-grid">
        <div className="card card-pad stack">
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <Field label={t('Название')} error={errors.name}><input className="input" value={form.name} onChange={set('name')} disabled={!writable} /></Field>
            <Field label={t('Алиас')} error={errors.alias} hint={t('Его передаёт код')}><input className="input input-mono" value={form.alias} onChange={set('alias')} disabled={!writable} /></Field>
          </div>
          <Field label={t('Тема')} error={errors.subject}><input className="input" value={form.subject} onChange={set('subject')} disabled={!writable} /></Field>
          <Field label="HTML" error={errors.html}>
            <textarea className="input input-mono" rows={14} value={form.html} onChange={set('html')} disabled={!writable} spellCheck={false} />
          </Field>
          <Field label={t('Текстовая версия')} hint={t('Для почтовых клиентов без HTML и для фильтров спама')}>
            <textarea className="input input-mono" rows={4} value={form.text} onChange={set('text')} disabled={!writable} />
          </Field>
        </div>

        <div className="stack">
          <div className="card">
            <div className="card-head">
              <h3>{t('Предпросмотр')}</h3>
              <div className="chips">
                <button className={`chip${tab === 'html' ? ' on' : ''}`} onClick={() => setTab('html')}>HTML</button>
                <button className={`chip${tab === 'text' ? ' on' : ''}`} onClick={() => setTab('text')}>{t('Текст')}</button>
              </div>
            </div>
            <div className="card-pad stack">
              <div className="small muted">{t('Тема:')} <strong style={{ color: 'var(--text)' }}>{preview?.subject || '—'}</strong></div>
              {tab === 'html' ? (
                // sandbox without allow-scripts: template HTML can never run code in the dashboard
                <iframe title={t('Предпросмотр письма')} sandbox="" srcDoc={preview?.html ?? `<p style="font-family:sans-serif;color:#888">${t('Нет HTML-версии')}</p>`} style={{ width: '100%', height: 360, border: '1px solid var(--border)', borderRadius: 12, background: '#eef4f9' }} />
              ) : (
                <pre className="code" style={{ whiteSpace: 'pre-wrap' }}>{preview?.text ?? t('Нет текстовой версии')}</pre>
              )}
              {!!preview?.missing.length && <div className="callout warn">{t('Не хватает переменных:')} {preview.missing.map((m) => <code key={m} style={{ marginRight: 6 }}>{m}</code>)} — {t('API вернёт 422, а не отправит пустоту.')}</div>}
            </div>
          </div>
          <div className="card card-pad stack">
            <div className="row">
              <h3 style={{ flex: 1 }}>{t('Тестовые переменные')}</h3>
              <button className="btn btn-sm" onClick={() => setVars(JSON.stringify(sampleFor(preview?.variables ?? [], parsedVars.value), null, 2))}>{t('Заполнить по шаблону')}</button>
            </div>
            <textarea className="input input-mono" rows={7} value={vars} onChange={(e) => setVars(e.target.value)} spellCheck={false} />
            {!parsedVars.ok && <span className="small" style={{ color: 'var(--coral)' }}>{t('Некорректный JSON')}</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-2" style={{ marginTop: 16 }}>
        <div className="card card-pad">
          <h3 style={{ marginBottom: 12 }}>{t('Отправка из кода')}</h3>
          <CodeTabs
            snippets={{
              SDK: `import { MailOcean } from '@mailocean/sdk';\n\nconst mo = new MailOcean(process.env.MAILOCEAN_API_KEY);\nawait mo.emails.send(${sendBody.replace(/\n/g, '\n  ')});`,
              cURL: `curl -X POST ${window.location.origin}/v1/emails \\\n  -H "Authorization: Bearer $MAILOCEAN_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '${sendBody.replace(/'/g, "'\\''")}'`,
            }}
          />
        </div>
        <div className="card">
          <div className="card-head"><h3>{t('Версии')}</h3></div>
          {!versions.data?.length ? (
            <Empty title={t('Появятся после сохранения')} />
          ) : (
            <table className="table">
              <tbody>
                {versions.data.map((v) => (
                  <tr key={v.version}>
                    <td><span className="pill sent">v{v.version}</span></td>
                    <td className="truncate">{v.subject}</td>
                    <td className="small muted" style={{ textAlign: 'right' }}>{ago(v.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
